import { Fragment, useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import { useMaxMd } from '@/hooks/useMaxMd'
import { usePageAccess } from '@/hooks/usePageAccess'
import { FixedAssetsSortableHeader } from '@/pages/fixed-assets/FixedAssetsSortableHeader'
import {
  filterRowsWithTree,
  toggleSingleColumnSort,
} from '@/pages/fixed-assets/fixedAssetsTableFilters'
import { fetchAllPurchasingProducts, fetchAllPurchasingVendorItems } from '@/pages/purchasing/purchasingFetchAll'
import { buildPurchasingVendorItemPackRequest } from '@/pages/purchasing/purchasingPackRequest'
import PurchasingPackSelect from '@/pages/purchasing/PurchasingPackSelect'
import {
  buildPurchasingVendorItemsExportMatrix,
  buildPurchasingVendorItemsFilterFields,
  fixedColumnStyle,
  isPurchasingVendorItemsColumnSortable,
  migrateLegacyPurchasingVendorItemsColumnWidths,
  PURCHASING_VENDOR_ITEMS_COLUMN_KEYS,
  PURCHASING_VENDOR_ITEMS_COLUMN_LABELS,
  PURCHASING_VENDOR_ITEMS_DEFAULT_SORT,
  PURCHASING_VENDOR_ITEMS_DEFAULT_WIDTHS,
  PURCHASING_VENDOR_ITEMS_FIELD_DEFINITIONS,
  PURCHASING_VENDOR_ITEMS_GROUP_OPTIONS,
  PURCHASING_VENDOR_ITEMS_PAGE_KEY,
  type PurchasingVendorItemsColumnKey,
} from '@/pages/purchasing/purchasingVendorItemsTableConfig'
import {
  exportPurchasingTableCsv,
  exportPurchasingTableToGoogleSheet,
} from '@/pages/purchasing/purchasingTableExport'
import {
  purchasingMatchStatusBadge,
  renderPurchasingStatusBadge,
} from '@/pages/purchasing/purchasingStatusBadges'
import {
  formatQboVendorLabel,
  qboVendorFilterOptions,
  useQboVendorDisplayNameMap,
} from '@/pages/purchasing/purchasingVendorNames'
import {
  createPurchasingProduct,
  editPurchasingVendorItemPack,
  matchPurchasingVendorItem,
  rejectPurchasingVendorItem,
  searchPurchasingMeezIngredients,
  type PurchasingVendorItemRow,
} from '@/services/purchasing'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import { formatLocalDateTime } from '@/utils/dateFormat'
import {
  ActiveFilterPillBar,
  ColumnPicker,
  FilterBuilderDrawer,
  GroupBySelect,
  SearchInput,
  SortBuilder,
  TablePageShell,
  TableToolbar,
  applySorts,
  countFilterConditions,
  generateFilterPills,
  getDefaultColumnVisibility,
  getVisibleFieldKeys,
  loadColumnVisibility,
  removeFilterPill,
  saveColumnVisibility,
  useColumnWidths,
  type ColumnVisibility,
  type FilterGroup,
  type SortDirection,
  type SortSpec,
} from '@/tableControls'

function distinctOptions(values: string[]) {
  return [...new Set(values.filter(Boolean))]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }))
}

function groupRows<T>(
  rows: T[],
  groupBy: string,
  getLabel: (row: T) => string,
): Array<{ label: string; items: T[] }> {
  if (!groupBy) return [{ label: '', items: rows }]
  const groups = new Map<string, T[]>()
  for (const row of rows) {
    const label = getLabel(row) || '—'
    const bucket = groups.get(label)
    if (bucket) bucket.push(row)
    else groups.set(label, [row])
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, items]) => ({ label, items }))
}

export default function PurchasingVendorItems() {
  const { canEdit } = usePageAccess('purchasing')
  const [rows, setRows] = useState<PurchasingVendorItemRow[]>([])
  const [productCatalog, setProductCatalog] = useState<SelectOption[]>([])
  const [defaultCogsAccountId, setDefaultCogsAccountId] = useState('')
  const [loading, setLoading] = useState(true)
  const [searchQ, setSearchQ] = useState('')
  const [filterTree, setFilterTree] = useState<FilterGroup | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showSort, setShowSort] = useState(false)
  const [showColumns, setShowColumns] = useState(false)
  const [sortSpecs, setSortSpecs] = useState<SortSpec[]>(PURCHASING_VENDOR_ITEMS_DEFAULT_SORT)
  const [groupBy, setGroupBy] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null)
  const [packText, setPackText] = useState('')
  const [packCount, setPackCount] = useState('')
  const [unitSizeQty, setUnitSizeQty] = useState('')
  const [unitSizeUom, setUnitSizeUom] = useState('')
  const [matchProductId, setMatchProductId] = useState<string | null>(null)
  const [matchMeezId, setMatchMeezId] = useState<string | null>(null)
  const [meezOptions, setMeezOptions] = useState<SelectOption[]>([])
  const [savingPack, setSavingPack] = useState(false)
  const [savingMatch, setSavingMatch] = useState(false)

  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() =>
    loadColumnVisibility(PURCHASING_VENDOR_ITEMS_PAGE_KEY, PURCHASING_VENDOR_ITEMS_FIELD_DEFINITIONS),
  )

  useEffect(() => {
    migrateLegacyPurchasingVendorItemsColumnWidths()
  }, [])

  const { getWidth, startResize } = useColumnWidths({
    pageKey: PURCHASING_VENDOR_ITEMS_PAGE_KEY,
    columnKeys: [...PURCHASING_VENDOR_ITEMS_COLUMN_KEYS],
    defaults: PURCHASING_VENDOR_ITEMS_DEFAULT_WIDTHS,
  })
  const maxMd = useMaxMd()
  const allowColumnResize = !maxMd

  const vendorNameMap = useQboVendorDisplayNameMap()

  const filterFields = useMemo(
    () =>
      buildPurchasingVendorItemsFilterFields(
        qboVendorFilterOptions(rows.map((row) => row.qbo_vendor_id), vendorNameMap),
        distinctOptions(
          rows.map((row) =>
            row.product_id != null ? String(row.product_id) : '',
          ),
        ).map((opt) => {
          const product = rows.find((row) => String(row.product_id) === opt.value)
          return {
            value: opt.value,
            label: product?.product_name ?? opt.label,
          }
        }),
      ),
    [rows, vendorNameMap],
  )

  const loadVendorItems = useCallback(async () => {
    try {
      setLoading(true)
      const trimmedQ = searchQ.trim()
      const data = trimmedQ
        ? await fetchAllPurchasingVendorItems({ q: trimmedQ })
        : await fetchAllPurchasingVendorItems()
      setRows(data)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load vendor items'))
    } finally {
      setLoading(false)
    }
  }, [searchQ])

  const loadProductCatalog = useCallback(async () => {
    try {
      const products = await fetchAllPurchasingProducts()
      setDefaultCogsAccountId(products[0]?.cogs_account_id ?? '')
      setProductCatalog(
        products.map((product) => ({
          id: String(product.id),
          label: product.name,
        })),
      )
    } catch {
      /* catalog is optional for rematch UX */
    }
  }, [])

  useEffect(() => {
    void loadVendorItems()
    void loadProductCatalog()
  }, [loadVendorItems, loadProductCatalog])

  const treeFiltered = useMemo(
    () => filterRowsWithTree(rows, filterTree, filterFields),
    [rows, filterTree, filterFields],
  )

  const sortedRows = useMemo(
    () => applySorts(treeFiltered, sortSpecs, PURCHASING_VENDOR_ITEMS_FIELD_DEFINITIONS),
    [treeFiltered, sortSpecs],
  )

  const selectedItem = useMemo(
    () => rows.find((row) => row.id === selectedItemId) ?? null,
    [rows, selectedItemId],
  )

  const visibleColumnKeys = useMemo(
    () => getVisibleFieldKeys(PURCHASING_VENDOR_ITEMS_FIELD_DEFINITIONS, columnVisibility),
    [columnVisibility],
  )

  const visibleTableWidth = useMemo(
    () => visibleColumnKeys.reduce((sum, key) => sum + getWidth(key), 0),
    [getWidth, visibleColumnKeys],
  )

  const tableStyle = useMemo((): CSSProperties | undefined => {
    if (!allowColumnResize) return undefined
    return {
      width: visibleTableWidth,
      minWidth: visibleTableWidth,
      tableLayout: 'fixed',
    }
  }, [allowColumnResize, visibleTableWidth])

  const headerStyle = useCallback(
    (columnKey: string): CSSProperties | undefined => {
      if (!allowColumnResize) return undefined
      return {
        ...fixedColumnStyle(getWidth(columnKey)),
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }
    },
    [allowColumnResize, getWidth],
  )

  const cellStyle = useCallback(
    (columnKey: string): CSSProperties | undefined => {
      if (!allowColumnResize) return undefined
      return fixedColumnStyle(getWidth(columnKey))
    },
    [allowColumnResize, getWidth],
  )

  const filterPills = useMemo(
    () => generateFilterPills(filterTree, filterFields),
    [filterTree, filterFields],
  )
  const activeFilterCount = useMemo(() => countFilterConditions(filterTree), [filterTree])
  const showingSubset = activeFilterCount > 0 || searchQ.trim().length > 0

  const getSortDirection = useCallback(
    (field: string): SortDirection | null => {
      const spec = sortSpecs.find((entry) => entry.field === field)
      return spec?.direction ?? null
    },
    [sortSpecs],
  )

  const handleRequestSort = useCallback((field: string) => {
    setSortSpecs((prev) => toggleSingleColumnSort(prev, field))
  }, [])

  const groupedSections = useMemo(() => {
    return groupRows(sortedRows, groupBy, (row) => {
      if (groupBy === 'qbo_vendor_id') {
        return formatQboVendorLabel(row.qbo_vendor_id, vendorNameMap)
      }
      if (groupBy === 'product_name') return row.product_name ?? '—'
      if (groupBy === 'match_status') return row.match_status
      return ''
    })
  }, [sortedRows, groupBy, vendorNameMap])

  useEffect(() => {
    if (!selectedItem) return
    setPackText(selectedItem.pack_text ?? '')
    setPackCount('')
    setUnitSizeQty('')
    setUnitSizeUom('')
    setMatchProductId(
      selectedItem.product_id != null ? String(selectedItem.product_id) : null,
    )
    setMatchMeezId(null)
    setMeezOptions([])
  }, [selectedItem])

  async function handleSavePack() {
    if (!selectedItem || !canEdit) return
    const payload = buildPurchasingVendorItemPackRequest({
      packText,
      packCount,
      unitSizeQty,
      unitSizeUom,
    })
    if (!payload) {
      toast.error('Enter pack text or all structured pack fields')
      return
    }
    setSavingPack(true)
    try {
      const result = await editPurchasingVendorItemPack(selectedItem.id, payload)
      toast.success(`Pack saved · recomputed ${result.recomputed_line_count} line(s)`)
      await loadVendorItems()
      setSelectedItemId(result.vendor_item_id)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save pack'))
    } finally {
      setSavingPack(false)
    }
  }

  async function handleMatch() {
    if (!selectedItem || !canEdit) return
    if (!matchProductId && !matchMeezId) {
      toast.error('Choose a product or Meez ingredient')
      return
    }
    setSavingMatch(true)
    try {
      const result = await matchPurchasingVendorItem(selectedItem.id, {
        product_id: matchProductId ? Number(matchProductId) : undefined,
        meez_ingredient_id: matchMeezId ?? undefined,
      })
      toast.success('Vendor item matched')
      setRows((prev) =>
        prev.map((row) =>
          row.id === result.vendor_item_id
            ? { ...row, product_id: result.product_id, match_status: result.match_status }
            : row,
        ),
      )
      await loadVendorItems()
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to match vendor item'))
    } finally {
      setSavingMatch(false)
    }
  }

  async function handleUnlink() {
    if (!selectedItem || !canEdit) return
    setSavingMatch(true)
    try {
      const result = await rejectPurchasingVendorItem(selectedItem.id)
      toast.success('Vendor item unlinked')
      setRows((prev) =>
        prev.map((row) =>
          row.id === result.vendor_item_id
            ? { ...row, product_id: null, product_name: null, match_status: result.match_status }
            : row,
        ),
      )
      setMatchProductId(null)
      await loadVendorItems()
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to unlink vendor item'))
    } finally {
      setSavingMatch(false)
    }
  }

  async function createProductOption(name: string): Promise<SelectOption | null> {
    const trimmed = name.trim()
    if (!trimmed) return null
    if (!trimmed || !defaultCogsAccountId) {
      toast.error('COGS account is required to create a product')
      return null
    }
    try {
      const product = await createPurchasingProduct({
        name: trimmed,
        atomic_uom: selectedItem?.atomic_uom ?? 'ea',
        cogs_account_id: defaultCogsAccountId,
      })
      const option = { id: String(product.id), label: product.name }
      setProductCatalog((prev) => [...prev, option])
      return option
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to create product'))
      return null
    }
  }

  function exportCsv() {
    const { headers, rows: exportRows } = buildPurchasingVendorItemsExportMatrix(sortedRows)
    exportPurchasingTableCsv('purchasing-vendor-items.csv', headers, exportRows)
  }

  async function exportGoogleSheet() {
    if (sortedRows.length === 0 || isExporting) return
    setIsExporting(true)
    try {
      const { headers, rows: exportRows } = buildPurchasingVendorItemsExportMatrix(sortedRows)
      const result = await exportPurchasingTableToGoogleSheet(
        'Purchasing vendor items',
        headers,
        exportRows,
      )
      toast.success(`Created Google Sheet: ${result.name}`)
      window.open(result.url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to export vendor items to Google Sheet'))
    } finally {
      setIsExporting(false)
    }
  }

  function renderCell(columnKey: PurchasingVendorItemsColumnKey, row: PurchasingVendorItemRow) {
    switch (columnKey) {
      case 'match_status': {
        const badge = purchasingMatchStatusBadge(row.match_status)
        return <span className={renderPurchasingStatusBadge(badge)}>{badge.label}</span>
      }
      case 'last_seen_at':
        return row.last_seen_at ? formatLocalDateTime(row.last_seen_at) : '—'
      case 'qbo_vendor_id':
        return formatQboVendorLabel(row.qbo_vendor_id, vendorNameMap)
      case 'product_name':
        return row.product_name ?? '—'
      default: {
        const value = row[columnKey as keyof PurchasingVendorItemRow]
        return value == null || value === '' ? '—' : String(value)
      }
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TablePageShell
        className="min-h-0 flex-1"
        rowCount={
          <div className="mb-2 text-xs text-gray-500 sm:text-sm">
            <span className="font-medium text-gray-700">
              {sortedRows.length.toLocaleString()} vendor item{sortedRows.length !== 1 ? 's' : ''}
            </span>
            {showingSubset ? (
              <span className="ml-1">(of {rows.length.toLocaleString()} loaded)</span>
            ) : null}
          </div>
        }
        toolbarLeft={
          <SearchInput
            value={searchQ}
            onChange={setSearchQ}
            placeholder="Search vendor items…"
            className="max-w-md flex-1"
          />
        }
        toolbarRight={
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => void loadVendorItems()}
              disabled={loading}
              aria-label="Refresh vendor items"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <TableToolbar
              groupBySlot={
                <GroupBySelect
                  value={groupBy}
                  onChange={setGroupBy}
                  options={PURCHASING_VENDOR_ITEMS_GROUP_OPTIONS}
                  aria-label="Group vendor items by"
                />
              }
              filterCount={activeFilterCount}
              sortCount={sortSpecs.length}
              showFilters={showFilters}
              showSort={showSort}
              showColumns={showColumns}
              onToggleFilters={() => setShowFilters((open) => !open)}
              onToggleSort={() => setShowSort((open) => !open)}
              onToggleColumns={() => setShowColumns((open) => !open)}
              exportOptions={[
                {
                  id: 'sheets',
                  label: 'Google Sheet',
                  onClick: () => void exportGoogleSheet(),
                  disabled: sortedRows.length === 0,
                },
                {
                  id: 'csv',
                  label: 'CSV',
                  onClick: exportCsv,
                  disabled: sortedRows.length === 0,
                },
              ]}
              defaultExportId="sheets"
              isExporting={isExporting}
              exportDisabled={sortedRows.length === 0}
            />
          </div>
        }
        filterPills={
          <ActiveFilterPillBar
            pills={filterPills}
            onRemovePill={(pillId) => setFilterTree(removeFilterPill(filterTree, pillId))}
            onClearAll={() => setFilterTree(null)}
          />
        }
      >
        <div className="table-scroll-mobile h-full min-h-0 overflow-auto overscroll-x-contain">
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-gray-500">
              <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
              Loading vendor items…
            </div>
          ) : (
            <table
              className={`table-forecast border-separate border-spacing-0${allowColumnResize ? '' : ' min-w-full'}`}
              style={tableStyle}
            >
              <thead>
                <tr>
                  {visibleColumnKeys.map((columnKey) => (
                    <FixedAssetsSortableHeader
                      key={columnKey}
                      label={
                        PURCHASING_VENDOR_ITEMS_COLUMN_LABELS[
                          columnKey as PurchasingVendorItemsColumnKey
                        ]
                      }
                      sortKey={columnKey}
                      sortDirection={getSortDirection(columnKey)}
                      onSort={handleRequestSort}
                      columnKey={columnKey}
                      style={headerStyle(columnKey)}
                      onStartResize={allowColumnResize ? startResize : undefined}
                      showResize={allowColumnResize && isPurchasingVendorItemsColumnSortable(columnKey)}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleColumnKeys.length}
                      className="py-12 text-center text-sm text-gray-600"
                    >
                      No vendor items match your search or filters.
                    </td>
                  </tr>
                ) : null}
                {groupedSections.map((section) => (
                  <Fragment key={section.label || 'all'}>
                    {section.label ? (
                      <tr className="bg-gray-50">
                        <td
                          colSpan={visibleColumnKeys.length}
                          className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
                        >
                          {section.label}
                          <span className="ml-2 font-normal normal-case text-gray-400">
                            ({section.items.length})
                          </span>
                        </td>
                      </tr>
                    ) : null}
                    {section.items.map((row) => (
                      <tr
                        key={row.id}
                        className={`cursor-pointer ${selectedItemId === row.id ? 'row-cursor' : ''}`}
                        onClick={() => setSelectedItemId(row.id)}
                      >
                        {visibleColumnKeys.map((columnKey) => (
                          <td key={columnKey} style={cellStyle(columnKey)}>
                            {renderCell(columnKey as PurchasingVendorItemsColumnKey, row)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </TablePageShell>

      <FilterBuilderDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        fields={filterFields}
        root={filterTree}
        onChange={setFilterTree}
        onClearAll={() => setFilterTree(null)}
      />

      <SortBuilder
        open={showSort}
        fields={PURCHASING_VENDOR_ITEMS_FIELD_DEFINITIONS}
        sorts={sortSpecs}
        onChange={setSortSpecs}
        onClose={() => setShowSort(false)}
        onClearAll={() => setSortSpecs(PURCHASING_VENDOR_ITEMS_DEFAULT_SORT)}
      />

      <ColumnPicker
        open={showColumns}
        fields={PURCHASING_VENDOR_ITEMS_FIELD_DEFINITIONS}
        visibility={columnVisibility}
        onChange={(visibility) => {
          setColumnVisibility(visibility)
          saveColumnVisibility(PURCHASING_VENDOR_ITEMS_PAGE_KEY, visibility)
        }}
        onClose={() => setShowColumns(false)}
        onReset={() => {
          const defaults = getDefaultColumnVisibility(PURCHASING_VENDOR_ITEMS_FIELD_DEFINITIONS)
          setColumnVisibility(defaults)
          saveColumnVisibility(PURCHASING_VENDOR_ITEMS_PAGE_KEY, defaults)
        }}
      />

      {selectedItem ? (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/10"
            onClick={() => setSelectedItemId(null)}
            aria-hidden="true"
          />
          <aside
            className="fixed right-0 top-0 z-50 flex h-full w-[min(720px,92vw)] flex-col border-l border-gray-200 bg-white shadow-xl"
            aria-label={`Vendor item ${selectedItem.vendor_item_code}`}
          >
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold text-gray-900">
                  {selectedItem.vendor_item_code}
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  {formatQboVendorLabel(selectedItem.qbo_vendor_id, vendorNameMap)} ·{' '}
                  {selectedItem.description ?? 'No description'}
                </p>
              </div>
              <Button
                variant="ghost"
                iconOnly
                aria-label="Close vendor item detail"
                onClick={() => setSelectedItemId(null)}
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 space-y-6">
              {canEdit ? (
                <section className="space-y-3 rounded-lg border border-gray-200 p-3">
                  <h3 className="text-sm font-semibold text-gray-900">Pack editor</h3>
                  <p className="text-xs text-gray-500">
                    User pack wins over invoice context. Pack drift resolution stays on Review.
                  </p>
                  <label className="block text-sm" htmlFor="vendor-items-pack-text">
                    <span className="font-medium text-gray-700">Pack text</span>
                    <div className="mt-1">
                      <PurchasingPackSelect
                        id="vendor-items-pack-text"
                        value={packText}
                        onChange={setPackText}
                      />
                    </div>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <label className="block text-sm">
                      <span className="font-medium text-gray-700">Count</span>
                      <input
                        className="input mt-1 w-full"
                        value={packCount}
                        onChange={(e) => setPackCount(e.target.value)}
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="font-medium text-gray-700">Unit size</span>
                      <input
                        className="input mt-1 w-full"
                        value={unitSizeQty}
                        onChange={(e) => setUnitSizeQty(e.target.value)}
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="font-medium text-gray-700">UOM</span>
                      <input
                        className="input mt-1 w-full"
                        value={unitSizeUom}
                        onChange={(e) => setUnitSizeUom(e.target.value)}
                      />
                    </label>
                  </div>
                  <Button
                    variant="primary"
                    disabled={savingPack}
                    onClick={() => void handleSavePack()}
                  >
                    {savingPack ? 'Saving pack…' : 'Save pack & recompute'}
                  </Button>
                </section>
              ) : null}

              {canEdit ? (
                <section className="space-y-3 rounded-lg border border-gray-200 p-3">
                  <h3 className="text-sm font-semibold text-gray-900">Product match</h3>
                  <label className="block text-sm">
                    <span className="font-medium text-gray-700">Product</span>
                    <SearchableSelect
                      className="mt-1"
                      options={productCatalog}
                      value={matchProductId}
                      onChange={(id) => setMatchProductId(id)}
                      placeholder="Search products…"
                      allowClear
                      allowCreate
                      createLabel="Create product"
                      onCreateNew={createProductOption}
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium text-gray-700">Meez ingredient</span>
                    <SearchableSelect
                      className="mt-1"
                      options={meezOptions}
                      value={matchMeezId}
                      onChange={(id) => setMatchMeezId(id)}
                      placeholder="Search Meez…"
                      allowClear
                      allowCreate
                      createLabel="Link Meez ingredient"
                      onCreateNew={async (name) => {
                        const options = await searchMeezIngredients(name)
                        setMeezOptions((prev) => {
                          const merged = [...prev]
                          for (const opt of options) {
                            if (!merged.some((entry) => entry.id === opt.id)) merged.push(opt)
                          }
                          return merged
                        })
                        return options[0] ?? null
                      }}
                    />
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="primary"
                      disabled={savingMatch}
                      onClick={() => void handleMatch()}
                    >
                      {savingMatch ? 'Saving…' : 'Match'}
                    </Button>
                    <Button
                      variant="danger"
                      disabled={savingMatch || !selectedItem.product_id}
                      onClick={() => void handleUnlink()}
                    >
                      Unlink
                    </Button>
                  </div>
                </section>
              ) : null}

              <section className="text-sm text-gray-600 space-y-1">
                <p>
                  Match status:{' '}
                  <span className={renderPurchasingStatusBadge(
                    purchasingMatchStatusBadge(selectedItem.match_status),
                  )}>
                    {purchasingMatchStatusBadge(selectedItem.match_status).label}
                  </span>
                </p>
                <p>Brand: {selectedItem.brand ?? '—'}</p>
                <p>Atomic UOM: {selectedItem.atomic_uom ?? '—'}</p>
                <p>Purchases: {selectedItem.purchase_count ?? '—'}</p>
              </section>
            </div>
          </aside>
        </>
      ) : null}
    </div>
  )
}

async function searchMeezIngredients(query: string): Promise<SelectOption[]> {
  const trimmed = query.trim()
  if (!trimmed) return []
  const { rows } = await searchPurchasingMeezIngredients({ q: trimmed, limit: 25 })
  return rows.map((row) => ({ id: row.id, label: row.name }))
}
